import React from 'react';

import { useIntl } from '../../../util/reactIntl';
import { formatMoney } from '../../../util/currency';
import { getConditionStyle } from '../../../util/listingConditions';
import { displayBrand, findEnumLabel } from '../../../util/listingFieldLabels';
import { types as sdkTypes } from '../../../util/sdkLoader';

import css from './ProductHeader.module.css';

const { Money } = sdkTypes;

/**
 * The right-column header on ListingPage:
 * - Title + price on one row (with the original retail price struck
 *   through before it, when the seller gave one)
 * - Size (raw value, uppercased) + condition pill on the next row
 * - Brand on its own row
 *
 * All meta rows are conditional — a listing without a brand or condition
 * collapses that row gracefully. The condition pill takes per-condition
 * colors from the same util ListingCard uses, so the visual cue stays
 * consistent across the marketplace.
 *
 * @param {Object} props
 * @param {Object} props.listing – denormalized listing entity
 * @param {Array} props.listingFields – `config.listing.listingFields`
 * @returns {JSX.Element}
 */
const ProductHeader = props => {
  const { listing, listingFields } = props;
  const intl = useIntl();

  const { title = '', price, publicData = {} } = listing.attributes || {};
  const sizeRaw = publicData['storrelse-klar'] || publicData['storrelse-sko'] || null;
  const sizeLabel = sizeRaw ? String(sizeRaw).toUpperCase() : null;

  const conditionRaw = publicData.tilstand;
  const conditionLabel = findEnumLabel(listingFields, 'tilstand', conditionRaw);
  const conditionStyle = getConditionStyle(conditionRaw);

  const brandLabel = displayBrand(listingFields, publicData);
  const isPremium = publicData.premium === 'premium';

  const formattedPrice = price ? formatMoney(intl, price) : null;

  // Nypris (original retail price) is stored as a plain ore integer on
  // publicData. Only show it when it's actually above the asking price —
  // a struck-through number that isn't a saving reads as a mistake.
  const nyprisAmount = publicData.nypris;
  const showNypris =
    typeof nyprisAmount === 'number' && !!price && nyprisAmount > price.amount;
  const formattedNypris = showNypris
    ? formatMoney(intl, new Money(nyprisAmount, price.currency))
    : null;

  return (
    <header className={css.root}>
      <div className={css.titleRow}>
        <h1 className={css.title}>{title}</h1>
        {formattedPrice ? (
          <p className={css.price}>
            {formattedNypris ? (
              <span
                className={css.nypris}
                title={intl.formatMessage({ id: 'ListingPage.nyprisLabel' })}
              >
                {formattedNypris}
              </span>
            ) : null}
            {formattedPrice}
          </p>
        ) : null}
      </div>

      {sizeLabel || conditionLabel ? (
        <div className={css.metaRow}>
          {sizeLabel ? <p className={css.size}>{sizeLabel}</p> : <span />}
          {conditionLabel ? (
            <span className={css.condition} style={conditionStyle || undefined}>
              {conditionLabel}
            </span>
          ) : null}
        </div>
      ) : null}

      {brandLabel || isPremium ? (
        <div className={css.brandRow}>
          {brandLabel ? <p className={css.brand}>{brandLabel}</p> : null}
          {isPremium ? <span className={css.premiumBadge}>Premium</span> : null}
        </div>
      ) : null}
    </header>
  );
};

export default ProductHeader;
